import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, Package, RotateCcw } from 'lucide-react';

import {
  useClearFeatureFlagOverride,
  useFeatureFlags,
  useUpdateFeatureFlag,
} from '@/lib/api/adminHooks';
import type {
  FeatureFlag,
  FeatureFlagKittingStats,
  FeatureFlagValue,
} from '@/types/admin';

/**
 * Backend feature flags — DB-backed runtime overrides on top of the env
 * (Railway) defaults. Toggles are STAGED locally; nothing changes until
 * "Confirm & apply", which writes overrides through the admin API. An
 * override reaches every backend process within ~15s (no deploy). "Revert
 * to env" deletes the override so the Railway value rules again.
 */

const CATEGORY_ORDER = [
  'Product launches',
  'PPI & pharmacy',
  'Notifications & lifecycle',
  'Support & AI',
  'Clinical operations',
];

function displayValue(flag: FeatureFlag, value: FeatureFlagValue): string {
  if (flag.value_type === 'boolean') return value ? 'ON' : 'OFF';
  return `${value} minute${value === 1 ? '' : 's'}`;
}

export function FeatureFlagsScreen() {
  const { data, isLoading, isError } = useFeatureFlags();
  const update = useUpdateFeatureFlag();
  const clearOverride = useClearFeatureFlagOverride();

  // key -> staged value (only keys whose staged value differs from effective)
  const [staged, setStaged] = useState<Record<string, FeatureFlagValue>>({});
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const [query, setQuery] = useState('');

  const flags = data?.flags ?? [];
  // Support-role viewers get a read-only page (server rejects their writes
  // with full_admin_required anyway). Default to read-only until loaded.
  const canEdit = data?.viewer_can_edit === true;
  const visibleFlags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flags;
    return flags.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q) ||
        f.env_var.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q)
    );
  }, [flags, query]);
  const byCategory = useMemo(() => {
    const m = new Map<string, FeatureFlag[]>();
    for (const f of visibleFlags) {
      const list = m.get(f.category) ?? [];
      list.push(f);
      m.set(f.category, list);
    }
    return m;
  }, [visibleFlags]);

  const dirty = Object.entries(staged).filter(([key, value]) => {
    const f = flags.find((x) => x.key === key);
    return f && f.effective !== value;
  });

  const toggle = (f: FeatureFlag) => {
    if (f.value_type !== 'boolean') return;
    setApplyError(null);
    setStaged((prev) => {
      const next = { ...prev };
      const current = f.key in prev ? prev[f.key] : f.effective;
      const flipped = !Boolean(current);
      if (flipped === f.effective) delete next[f.key];
      else next[f.key] = flipped;
      return next;
    });
  };

  const stageNumber = (f: FeatureFlag, value: number) => {
    setApplyError(null);
    setStaged((prev) => {
      const next = { ...prev };
      if (value === f.effective) delete next[f.key];
      else next[f.key] = value;
      return next;
    });
  };

  const applyAll = async () => {
    setApplying(true);
    setApplyError(null);
    const failures: string[] = [];
    for (const [key, value] of dirty) {
      try {
        await update.mutateAsync({ key, value });
      } catch (err) {
        failures.push(`${key}: ${String(err)}`);
      }
    }
    setApplying(false);
    if (failures.length) {
      setApplyError(`Some changes failed — ${failures.join('; ')}`);
      // keep failed keys staged; drop the succeeded ones
      setStaged((prev) => {
        const next: Record<string, FeatureFlagValue> = {};
        for (const f of failures) {
          const k = f.split(':')[0];
          if (k in prev) next[k] = prev[k];
        }
        return next;
      });
    } else {
      setStaged({});
    }
  };

  return (
    <div className="flex flex-col gap-24 pb-[96px]">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Feature Flags</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Backend feature flags. Toggling stages a change; nothing happens until
          you confirm. Confirmed changes are stored as overrides on top of the
          Railway env defaults and reach every backend process within ~15
          seconds — no deploy. “Revert to env” removes the override.
        </p>
      </div>

      {data && !canEdit && (
        <p className="type-label-sm text-ink-muted border border-line rounded-lg bg-raised p-12">
          You have the <b>Support</b> role — flags are read-only here. A full
          Admin can change them.
        </p>
      )}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search flags by name or description…"
        aria-label="Search feature flags"
        className="w-full md:w-[360px] px-12 py-9 rounded-lg border border-line bg-raised type-body-sm placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
      />

      {isLoading && (
        <p className="type-body-md text-ink-muted">Loading flags…</p>
      )}
      {!isLoading && query.trim() && visibleFlags.length === 0 && (
        <p className="type-body-md text-ink-muted">
          No flags match “{query.trim()}”.
        </p>
      )}
      {isError && (
        <p className="type-body-md text-danger">
          Couldn't load feature flags — is the backend reachable?
        </p>
      )}

      {CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => (
        <section key={category} className="flex flex-col gap-10">
          <h2 className="type-label-md font-semibold text-ink-muted uppercase tracking-wider">
            {category}
          </h2>
          <div className="border border-line rounded-lg bg-raised divide-y divide-line">
            {byCategory.get(category)!.map((f) => (
              <FlagRow
                key={f.key}
                flag={f}
                stagedValue={f.key in staged ? staged[f.key] : null}
                kitting={
                  f.key === 'ppi_kitting_enabled' ? data?.kitting : undefined
                }
                onToggle={() => canEdit && toggle(f)}
                onNumberChange={(value) => canEdit && stageNumber(f, value)}
                readOnly={!canEdit}
                onRevert={() => clearOverride.mutate(f.key)}
                reverting={clearOverride.isPending}
              />
            ))}
          </div>
        </section>
      ))}

      {dirty.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 md:left-[230px] z-40 border-t border-line bg-raised/95 backdrop-blur">
          <div className="max-w-content mx-auto px-15 md:px-40 py-12 flex flex-col gap-8">
            <div className="flex flex-wrap items-center gap-10">
              <span className="type-label-sm font-semibold">
                {dirty.length} unsaved change{dirty.length > 1 ? 's' : ''}:
              </span>
              {dirty.map(([key, value]) => {
                const f = flags.find((x) => x.key === key)!;
                return (
                  <span
                    key={key}
                    className={`type-label-sm px-8 py-3 rounded border ${
                      f.caution
                        ? 'border-warning text-warning'
                        : 'border-line text-ink-muted'
                    }`}
                  >
                    {f.caution && (
                      <AlertTriangle size={12} className="inline mr-4 -mt-2" />
                    )}
                    {f.title}: {displayValue(f, f.effective)} →{' '}
                    {displayValue(f, value)}
                  </span>
                );
              })}
            </div>
            {applyError && (
              <p className="type-label-sm text-danger">{applyError}</p>
            )}
            <div className="flex items-center gap-10">
              <button
                type="button"
                disabled={applying}
                onClick={applyAll}
                className="type-label-sm px-14 py-8 rounded bg-ink text-page font-medium disabled:opacity-50"
              >
                {applying ? 'Applying…' : 'Confirm & apply'}
              </button>
              <button
                type="button"
                disabled={applying}
                onClick={() => {
                  setStaged({});
                  setApplyError(null);
                }}
                className="type-label-sm px-14 py-8 rounded border border-line hover:bg-raised"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FlagRow({
  flag,
  stagedValue,
  kitting,
  onToggle,
  onNumberChange,
  onRevert,
  reverting,
  readOnly,
}: {
  flag: FeatureFlag;
  stagedValue: FeatureFlagValue | null;
  kitting?: FeatureFlagKittingStats;
  onToggle: () => void;
  onNumberChange: (value: number) => void;
  onRevert: () => void;
  reverting: boolean;
  readOnly?: boolean;
}) {
  const shown = stagedValue ?? flag.effective;
  const isStaged = stagedValue !== null && stagedValue !== flag.effective;

  return (
    <div className="p-16 flex flex-col gap-6">
      <div className="flex items-start gap-12">
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-8">
            <span className="type-label-md font-semibold">{flag.title}</span>
            <code className="text-10 text-ink-muted border border-line px-5 py-1 rounded">
              {flag.env_var}
            </code>
            {flag.caution && (
              <span className="type-label-sm text-warning border border-warning px-6 py-1 rounded inline-flex items-center gap-4">
                <AlertTriangle size={12} /> caution
              </span>
            )}
            {!flag.available && (
              <span className="type-label-sm text-ink-muted border border-dashed border-line px-6 py-1 rounded">
                pending deploy
              </span>
            )}
            {isStaged && (
              <span className="type-label-sm text-info border border-info px-6 py-1 rounded">
                staged
              </span>
            )}
          </div>
          <p className="type-body-sm text-ink-muted">{flag.description}</p>
          {kitting && (
            <p className="type-label-sm text-ink inline-flex items-center gap-6 mt-2">
              <Package size={14} />
              Kits sent: <b>{kitting.kits_sent_total}</b> total ·{' '}
              <b>{kitting.kits_sent_30d}</b> last 30d
              {kitting.tins_sent_total > 0 && (
                <> · {kitting.tins_sent_total} tins</>
              )}
            </p>
          )}
          <p className="type-label-sm text-ink-muted">
            {flag.override === null ? (
              <>Env default ({displayValue(flag, flag.env_default)})</>
            ) : (
              <>
                Override {displayValue(flag, flag.override)}
                {flag.updated_by && <> · set by {flag.updated_by}</>}
                {flag.updated_at && (
                  <> · {format(parseISO(flag.updated_at), 'MMM d, h:mm a')}</>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    disabled={reverting}
                    onClick={onRevert}
                    className="ml-8 inline-flex items-center gap-4 underline hover:text-ink disabled:opacity-50"
                    title="Delete the override — the Railway env value rules again"
                  >
                    <RotateCcw size={11} /> Revert to env
                  </button>
                )}
              </>
            )}
          </p>
        </div>
        {flag.value_type === 'boolean' ? (
          <ToggleSwitch
            on={Boolean(shown)}
            disabled={!flag.available || readOnly}
            onClick={onToggle}
            label={`Toggle ${flag.title}`}
          />
        ) : (
          <NumberSetting
            value={Number(shown)}
            minimum={flag.minimum}
            maximum={flag.maximum}
            step={flag.step}
            disabled={!flag.available || readOnly}
            onChange={onNumberChange}
          />
        )}
      </div>
    </div>
  );
}

function NumberSetting({
  value,
  minimum,
  maximum,
  step,
  disabled,
  onChange,
}: {
  value: number;
  minimum: number | null;
  maximum: number | null;
  step: number | null;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-6 shrink-0 type-label-sm text-ink-muted">
      <input
        type="number"
        value={value}
        min={minimum ?? undefined}
        max={maximum ?? undefined}
        step={step ?? 1}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.valueAsNumber;
          if (Number.isInteger(next)) onChange(next);
        }}
        className="w-80 rounded border border-line bg-page px-8 py-6 text-right type-body-sm text-ink disabled:opacity-40"
      />
      min
    </label>
  );
}

function ToggleSwitch({
  on,
  disabled,
  onClick,
  label,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  // Steel (--accent) = ON, sunken track + border = OFF. The explicit
  // On/Off word removes any ambiguity about which state the pill is in.
  return (
    <div className="flex items-center gap-8 shrink-0">
      <span
        className={`type-label-sm w-[24px] text-right ${
          on ? 'text-accent font-medium' : 'text-ink-muted'
        }`}
      >
        {on ? 'On' : 'Off'}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={`relative block shrink-0 w-[44px] h-[24px] rounded-full border transition-colors disabled:opacity-40 ${
          on ? 'bg-accent border-accent' : 'bg-sunken border-line-strong'
        }`}
      >
        <span
          className={`absolute top-[1px] left-0 w-[20px] h-[20px] rounded-full bg-white border border-line shadow transition-transform ${
            on ? 'translate-x-[21px]' : 'translate-x-[2px]'
          }`}
        />
      </button>
    </div>
  );
}
